import React from "react";
import classNames from "classnames/dedupe";
import {
  ProductTitle,
  IProductTitleProps,
} from "../../DataDisplay/ProductTitle";
import { Image, IImageProps } from "../../Utility/MediaBuilder/Image";
import { Typography } from "../../Fundamentals/Typography";
import { Button } from "../../ActionsSelectors/Button";
import { ProductCard, IProductCardData } from "../../ProductCard";
import { Carousel } from "../../ActionsSelectors/Carousel";
import { GRID_BREAKPOINTS } from "../../Fundamentals/Grid/Column";

export interface IBlogProps<LinkType = HTMLLinkElement> {
  articleVariant?: "blog" | "product review";
  /**
   * Labels, images and text content
   */
  labels: {
    blogTitle?: string;
    blogType?: string;
    blogContent: string;
    buttonOne?: string;
    buttonTwo?: string;
  };
  blogImage?: IImageProps;
  /**
   * Title for product review section with links to brand and PDP page
   */
  productReviewTitle?: IProductTitleProps;
  /**
   * Product cards content
   */
  productCards?: IProductCardData<LinkType>[];
  /**
   * The custom set of css classes used to style component
   */
  classes?: {
    /**
     * applied to outer 'div' element
     */
    root?: string;
  };
}

function Blog<LinkType>({
  articleVariant = "blog",
  blogImage,
  labels,
  productReviewTitle,
  productCards,
  classes,
}: IBlogProps<LinkType>): JSX.Element {
  const rootClasses = classNames(classes?.root, "py-8", {
    "bg-color-grey-100 px-6 px-md-7 py-md-9":
      articleVariant === "product review",
  });

  const buttonOneOnChange = () => {
    return null;
  };

  const buttonTwoOnChange = () => {
    return null;
  };

  return (
    <div className={rootClasses}>
      {labels?.blogType && (
        <Typography
          variant="text-small-header"
          as="h3"
          classes={{ root: "mb-6" }}
        >
          {labels?.blogType}
        </Typography>
      )}

      {labels?.blogTitle && (
        <Typography variant="h2-enriched" as="h2" classes={{ root: "mb-6" }}>
          {labels?.blogTitle}
        </Typography>
      )}

      <ProductTitle
        brandName={productReviewTitle?.brandName}
        brandLink={productReviewTitle?.brandLink}
        productName={productReviewTitle?.productName}
        productLink={productReviewTitle?.productLink}
        variant="PLP"
      />

      {blogImage && (
        <div className="ratio ratio-5x4 ratio-img my-7 my-md-9">
          <Image src={blogImage?.src} alt={blogImage?.alt} />
        </div>
      )}

      <p className="p-medium mb-7 mb-md-9" style={{ whiteSpace: "pre-wrap" }}>
        {labels?.blogContent}
      </p>

      <div className="d-flex">
        {labels?.buttonOne && (
          <Button
            variant="primary"
            size="small"
            classes={{ root: "mt-3 mt-xms-0 me-4" }}
            onChange={buttonOneOnChange()}
          >
            {labels?.buttonOne}
          </Button>
        )}

        {labels?.buttonTwo && (
          <Button
            variant="primary"
            size="small"
            classes={{ root: "mt-3 mt-xms-0" }}
            onChange={buttonTwoOnChange()}
          >
            {labels?.buttonTwo}
          </Button>
        )}
      </div>
      {productCards?.length ? (
        <Carousel
          variant="cards-list-carousel"
          params={{
            mainCarouselParams: {
              slidesPerView: 1,
              breakpoints: {
                [GRID_BREAKPOINTS.sm]: {
                  slidesPerView: 2,
                },
                [GRID_BREAKPOINTS.md]: {
                  slidesPerView: 3,
                },
                [GRID_BREAKPOINTS.lgm]: {
                  slidesPerView: 4,
                },
                [GRID_BREAKPOINTS.xl]: {
                  slidesPerView: 6,
                },
              },
            },
          }}
        >
          {productCards.map((product) => (
            <ProductCard<LinkType>
              key={product.description.product.text as string}
              productCardData={product}
              isFavoriteAvailable={false}
            />
          ))}
        </Carousel>
      ) : null}
    </div>
  );
}

export default Blog;
